/**
 * Email Service
 * Handles all email operations with the backend API
 */

const API_BASE_URL = 'https://recotrackapi.onrender.com/api/PortfolioEmails/send';
const CLIENT_ID = 'web-ui-v1.0';

/**
 * Email type definitions
 */
export type EmailType = 'QUERRY' | 'GET_IN_TOUCH' | 'CONNECT';

/**
 * Query Email Payload Interface
 */
export interface QueryEmailPayload {
  type: 'QUERRY';
  data: {
    fullName: string;
    designation: string;
    emailAddress: string;
    phoneNumber: string;
    querry: string;
  };
}

/**
 * Get In Touch Email Payload Interface
 */
export interface GetInTouchEmailPayload {
  type: 'GET_IN_TOUCH';
  data: {
    fullName: string;
    email: string;
    subject: string;
    message: string;
  };
}

/**
 * Connect Email Payload Interface
 */
export interface ConnectEmailPayload {
  type: 'CONNECT';
  data: {
    senderName: string;
    senderEmail: string;
    message: string;
  };
}

/**
 * Union type for all email payloads
 */
export type EmailPayload = QueryEmailPayload | GetInTouchEmailPayload | ConnectEmailPayload;

/**
 * API Response type
 */
export interface EmailApiResponse {
  success: boolean;
  message: string;
  statusCode?: number;
}

/**
 * Custom error class for email operations
 */
export class EmailServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: any
  ) {
    super(message);
    this.name = 'EmailServiceError';
  }
}

/**
 * Sends a query email
 */
export const sendQueryEmail = async (
  fullName: string,
  designation: string,
  emailAddress: string,
  phoneNumber: string,
  querry: string
): Promise<EmailApiResponse> => {
  const payload: QueryEmailPayload = {
    type: 'QUERRY',
    data: {
      fullName,
      designation,
      emailAddress,
      phoneNumber,
      querry,
    },
  };

  return sendEmail(payload);
};

/**
 * Sends a "Get in Touch" email
 */
export const sendGetInTouchEmail = async (
  fullName: string,
  email: string,
  subject: string,
  message: string
): Promise<EmailApiResponse> => {
  const payload: GetInTouchEmailPayload = {
    type: 'GET_IN_TOUCH',
    data: {
      fullName,
      email,
      subject,
      message,
    },
  };

  return sendEmail(payload);
};

/**
 * Sends a "Connect" email
 */
export const sendConnectEmail = async (
  senderName: string,
  senderEmail: string,
  message: string
): Promise<EmailApiResponse> => {
  const payload: ConnectEmailPayload = {
    type: 'CONNECT',
    data: {
      senderName,
      senderEmail,
      message,
    },
  };

  return sendEmail(payload);
};

/**
 * Generic email sending function
 */
const sendEmail = async (payload: EmailPayload): Promise<EmailApiResponse> => {
  try {
    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Accept': '*/*',
        'X-Client-Id': CLIENT_ID,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    // Check if API returned success status (status: "sent") or HTTP response is ok
    if (data.status === 'sent' || response.ok) {
      return {
        success: true,
        message: data.message || 'Email sent successfully',
        statusCode: response.status,
      };
    }

    throw new EmailServiceError(
      data.message || 'Failed to send email',
      response.status,
      data
    );
  } catch (error) {
    if (error instanceof EmailServiceError) {
      throw error;
    }

    throw new EmailServiceError(
      'Network error or failed to send email',
      undefined,
      error
    );
  }
};

/**
 * Validates email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates phone number format (international format)
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/\s|-|\(|\)/g, ''));
};
